import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { AlertNotice, AlertMessage } from 'shared/components';
import SecurityQuestion from './security-question/SecurityQuestion';
import * as tcRenewalAction from 'features/tc-renewal/duck';
import { getMappedContactTypes } from 'features/tc-renewal/selectors';
import { CONTACT_TYPES } from 'shared/constants';

const YES_VALUE = 'Yes';
const NO_VALUE = 'No';

const SecurityInformationQuestions = [
  {
    id: 1,
    name: 'secureMaterialsReceiptContacts',
    question: 'testCenter.securityInfo.securityMaterialsReceiptLabel',
    responsible: 'testCenter.securityInfo.securityMaterialsReceiptResponsible',
    contactType: 'securityMaterialsReceipt',
    listOfPersons: 'secureMaterialsReceiptContacts',
    modalMessage: 'testCenter.securityInfo.securityMaterialsReceiptModal',
    sectionTitle: 'testCenter.securityInfo.securityMaterialsReceiptResponsible',
  },
  {
    id: 2,
    name: 'secureMaterialsCheckinContacts',
    question: 'testCenter.securityInfo.securityMaterialsCheckinLabel',
    responsible: 'testCenter.securityInfo.securityMaterialsCheckinResponsible',
    contactType: 'securityMaterialsCheckin',
    listOfPersons: 'secureMaterialsCheckinContacts',
    modalMessage: 'testCenter.securityInfo.securityMaterialsCheckinModal',
    sectionTitle: 'testCenter.securityInfo.securityMaterialsCheckinResponsible',
  },
  {
    id: 3,
    name: 'secureAdministerTestContacts',
    question: 'testCenter.securityInfo.securityAdministerTestLabel',
    responsible: 'testCenter.securityInfo.securityAdministerTestResponsible',
    contactType: 'securityAdministerTest',
    listOfPersons: 'secureAdministerTestContacts',
    modalMessage: 'testCenter.securityInfo.securityAdministerTestModal',
    sectionTitle: 'testCenter.securityInfo.securityAdministerTestResponsible',
  },
  {
    id: 4,
    name: 'secureMaterialsReturnContacts',
    question: 'testCenter.securityInfo.securityMaterialsReturn',
    responsible: 'testCenter.securityInfo.securityMaterialsReturnResponsible',
    contactType: 'securityMaterialsReturn',
    listOfPersons: 'secureMaterialsReturnContacts',
    modalMessage: 'testCenter.securityInfo.securityMaterialsReturnModal',
    sectionTitle: 'testCenter.securityInfo.securityMaterialsReturnResponsible',
  },
];

class TCSecurityInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tcCycle: {
        ...props.tcCycle,
      },
      answers: {},
      showSuccessMessage: false,
      successMessage: '',
      errors: {},
      isDirty: false,
    };

    this.handleContinue = this.handleContinue.bind(this);
    this.updateContactsList = this.updateContactsList.bind(this);
    this.removeSuccessMessage = this.removeSuccessMessage.bind(this);
    this.getContacts = this.getContacts.bind(this);
    this.onSelectionChange = this.onSelectionChange.bind(this);
    this.checkIsDirty = this.checkIsDirty.bind(this);
  }

  componentDidMount() {
    const { actions, mappedContactTypes } = this.props;

    if (isEmpty(mappedContactTypes)) {
      actions.getContactTypes().then(() => this.getContacts());
    } else {
      this.getContacts();
    }
  }

  getContacts() {
    const {
      tcCycle,
      actions,
      alternateContacts,
      secureMaterialsReceiptContacts,
      secureMaterialsCheckinContacts,
      secureAdministerTestContacts,
      secureMaterialsReturnContacts,
      mappedContactTypes,
    } = this.props;

    if (!alternateContacts.length) {
      actions.getTcCycleContacts(tcCycle.id, mappedContactTypes[CONTACT_TYPES.alternateContact]);
    }
    if (!secureMaterialsReceiptContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityMaterialsReceipt]
      );
    }
    if (!secureMaterialsCheckinContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityMaterialsCheckin]
      );
    }
    if (!secureAdministerTestContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityAdministerTest]
      );
    }
    if (!secureMaterialsReturnContacts.length) {
      actions.getTcCycleContacts(
        tcCycle.id,
        mappedContactTypes[CONTACT_TYPES.securityMaterialsReturn]
      );
    }
  }

  async updateContactsList(currentPerson, listOfPersons, remove) {
    const { tcCycle } = this.props;

    if (remove) {
      return await this.props.actions.removeTcCycleContact(currentPerson.id).then(() => {
        if (isEmpty(this.props.errorRemovingContact)) {
          this.props.actions.getTcCycleContactsAction({
            data: listOfPersons,
            payload: { typeId: currentPerson.contactType.id },
          });
          this.setState({
            showSuccessMessage: true,
            successMessage: 'testCenter.securityInfo.successRemoveMessage',
          });
        }
      });
    }
    if (!currentPerson.id) {
      currentPerson.testCenterCycle = { id: tcCycle.id };

      return await this.props.actions.setTcCycleContact(currentPerson).then(() => {
        if (isEmpty(this.props.errorSetContact)) {
          this.props.actions.getTcCycleContacts(tcCycle.id, currentPerson.contactType.id);
          this.setState({
            errors: {},
            showSuccessMessage: true,
            successMessage: 'testCenter.securityInfo.successAddMessage',
          });
        }
      });
    }

    return await this.props.actions.setTcCycleContact(currentPerson).then(() => {
      if (isEmpty(this.props.errorSetContact)) {
        this.setState({
          errors: {},
          showSuccessMessage: true,
          successMessage: 'testCenter.securityInfo.successEditMessage',
        });
      }

      this.props.actions.getTcCycleContactsAction({
        data: listOfPersons,
        payload: { typeId: currentPerson.contactType.id },
      });
    });
  }

  onSelectionChange(question, value) {
    let returnObj = this.state.answers;
    returnObj[question] = value;
    this.setState({
      answers: returnObj,
      isDirty: true,
    });
  }

  removeSuccessMessage() {
    this.setState({
      showSuccessMessage: false,
      successMessage: '',
    });
  }

  async handleContinue() {
    const self = this;
    let errors = {};

    const { answers } = this.state;
    const { tcCycle, mappedContactTypes, actions } = this.props;

    if (!isEmpty(answers)) {
      for (const key in answers) {
        if (
          answers[key] === NO_VALUE &&
          (self.props[key] === undefined || self.props[key].length === 0)
        ) {
          errors[key] = ['errors.addPerson'];
        }

        if (
          answers[key] === YES_VALUE &&
          self.props[key] !== undefined &&
          self.props[key].length !== 0
        ) {
          for (const i = 0; i < self.props[key].length; i++) {
            await actions.removeTcCycleContact(self.props[key][i].id);
          }
          let question = SecurityInformationQuestions.find((q) => q.name === key);
          await actions.getTcCycleContacts(
            tcCycle.id,
            mappedContactTypes[CONTACT_TYPES[question.contactType]]
          );
        }
      }
    }
    this.setState({
      errors: errors,
    });
    return isEmpty(errors) ? true : false;
  }

  checkIsDirty() {
    return this.state.isDirty;
  }

  render() {
    const { alternateContacts, mappedContactTypes } = this.props;
    const { showSuccessMessage, successMessage, errors } = this.state;

    if (!alternateContacts.length) {
      return null;
    }

    return (
      <section className="security-information-container">
        <h4>{this.props.t('testCenter.securityInfo.title')}</h4>
        <p>{this.props.t('testCenter.securityInfo.subtitle')}</p>
        {!isEmpty(errors) && (
          <AlertNotice type="error" message={this.props.t('testCenter.mandatoryFieldsWarning')} />
        )}
        {SecurityInformationQuestions.map((question, idx) => (
          <SecurityQuestion
            {...question}
            contactTypeId={mappedContactTypes[CONTACT_TYPES[question.contactType]]}
            alternateContact={alternateContacts[0]}
            listOfPersons={this.props[question.listOfPersons]}
            updateContactsList={this.updateContactsList}
            onSelectionChange={this.onSelectionChange}
            error={errors[question.name]}
            key={idx}
          />
        ))}
        <AlertMessage
          show={showSuccessMessage}
          removeMessage={this.removeSuccessMessage}
          message={successMessage}
        />
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    tcCycle: state.tcRenewal.tcCycle,
    currentUser: state.users.currentUser,
    mappedContactTypes: getMappedContactTypes(state),
    alternateContacts: state.tcRenewal.alternateContacts,
    secureMaterialsReceiptContacts: state.tcRenewal.secureMaterialsReceiptContacts,
    secureMaterialsCheckinContacts: state.tcRenewal.secureMaterialsCheckinContacts,
    secureAdministerTestContacts: state.tcRenewal.secureAdministerTestContacts,
    secureMaterialsReturnContacts: state.tcRenewal.secureMaterialsReturnContacts,
    errorSetContact: state.tcRenewal.errorSetContact,
    errorRemovingContact: state.tcRenewal.errorRemovingContact,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...tcRenewalAction }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(withTranslation(null, { withRef: true })(TCSecurityInformation));

TCSecurityInformation.propTypes = {
  tcCycle: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape().isRequired,
  mappedContactTypes: PropTypes.shape({}),
  alternateContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureMaterialsReceiptContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureMaterialsCheckinContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureAdministerTestContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  secureMaterialsReturnContacts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  actions: PropTypes.shape({
    getTcCycleContacts: PropTypes.func,
    removeTcCycleContact: PropTypes.func,
    setTcCycleContact: PropTypes.func,
    getTcCycleContactsAction: PropTypes.func,
  }).isRequired,
  t: PropTypes.func.isRequired,
};
